import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ChevronDown, X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import axiosInstance from '@/api/axios.js';
import * as d3 from 'd3';
import useResizeObserver from 'use-resize-observer';

const TOOLTIP_ID = 'contamination-chart-tooltip';

const BELGIAN_BIN_IDS = [
    'R2', 'R3', 'R4', 'R6', 'R7', 'R8', 'R9', 'R10',
    'R12', 'R14', 'R15', 'R16', 'R17', 'R19', 'R20', 'R21', 'R23', 'R24', 'R25', 'R28'
];

const getOrCreateTooltip = (isDarkMode) => {
    let tooltip = d3.select(`#${TOOLTIP_ID}`);
    if (!tooltip.empty()) {
        tooltip
            .style('background-color', isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)')
            .style('color', isDarkMode ? '#ffffff' : '#000000')
            .style('box-shadow', isDarkMode ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.15)')
            .style('border', isDarkMode ? '1px solid #444' : '1px solid #DDD');
        return tooltip;
    }
    return d3.select('body')
        .append('div')
        .attr('id', TOOLTIP_ID)
        .style('position', 'fixed')
        .style('pointer-events', 'none')
        .style('visibility', 'hidden')
        .style('background-color', isDarkMode ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)')
        .style('color', isDarkMode ? '#ffffff' : '#000000')
        .style('border-radius', '8px')
        .style('box-shadow', isDarkMode ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.15)')
        .style('border', isDarkMode ? '1px solid #444' : '1px solid #DDD')
        .style('padding', '12px')
        .style('font-size', '14px')
        .style('line-height', '1.5')
        .style('z-index', '9999')
        .style('max-width', '320px')
        .style('transition', 'opacity 0.1s ease');
};

const hideTooltip = () => d3.select(`#${TOOLTIP_ID}`).style('visibility', 'hidden');

const showTooltip = (event, html, isDarkMode) => {
    const tooltip = getOrCreateTooltip(isDarkMode);
    const x = event.clientX + 14;
    const y = event.clientY - 14;
    const node = tooltip.node();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = node ? node.offsetWidth || 320 : 320;
    const th = node ? node.offsetHeight || 150 : 150;
    tooltip
        .html(html)
        .style('visibility', 'visible')
        .style('left', (x + tw > vw ? x - tw - 20 : x) + 'px')
        .style('top',  (y + th > vh ? y - th      : y) + 'px');
};

const moveTooltip = (event) => {
    const tooltip = d3.select(`#${TOOLTIP_ID}`);
    if (tooltip.style('visibility') === 'hidden') return;
    const x = event.clientX + 14;
    const y = event.clientY - 14;
    const node = tooltip.node();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = node ? node.offsetWidth || 320 : 320;
    const th = node ? node.offsetHeight || 150 : 150;
    tooltip
        .style('left', (x + tw > vw ? x - tw - 20 : x) + 'px')
        .style('top',  (y + th > vh ? y - th      : y) + 'px');
};

const formatCapture = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Amsterdam',
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit',
    }).format(d);
};

const STREAM_NORMALISE = (name) => {
    const l = (name || '').toLowerCase();
    if (l === 'organic')                  return 'Organic';
    if (l === 'pmd' || l === 'plastic')   return 'PMD';
    if (l === 'paper')                    return 'Paper';
    if (l === 'residual' || l === 'rest') return 'Residual';
    return null;
};

const mediaMatchesStream = (item, streamName) => {
    if (item.waste_stream) return STREAM_NORMALISE(item.waste_stream) === streamName;
    return (item.label || '').toLowerCase().includes(streamName.toLowerCase());
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ items, index, onClose, onPrev, onNext, signedUrls, onRequestSign }) {
    const item = items[index];

    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape')     onClose();
            if (e.key === 'ArrowLeft')  onPrev();
            if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose, onPrev, onNext]);

    useEffect(() => {
        const wanted = [items[index], items[index - 1], items[index + 1]]
            .filter(Boolean)
            .map(i => i.file_path);
        onRequestSign(wanted);
    }, [index, items, onRequestSign]);

    const src = signedUrls[item.file_path];

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.85)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
             onClick={onClose}>
            <button onClick={onClose}
                style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
                         color: 'rgba(255,255,255,0.8)', cursor: 'pointer', padding: 4 }}>
                <X size={24} />
            </button>
            {items.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
                    style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                             background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                             color: '#fff', cursor: 'pointer', padding: 8 }}>
                    <ChevronLeft size={20} />
                </button>
            )}
            <div style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                         alignItems: 'center', gap: 12 }}
                 onClick={(e) => e.stopPropagation()}>
                {src ? (
                    item.media_type === 'video'
                        ? <video controls autoPlay style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 8 }} src={src} />
                        : <img src={src} alt={item.label || 'media'}
                               style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 8, objectFit: 'contain' }} />
                ) : (
                    <div className="animate-pulse" style={{ width: 400, height: 300, maxWidth: '90vw',
                                  borderRadius: 8, background: 'rgba(255,255,255,0.1)' }} />
                )}
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, textAlign: 'center' }}>
                    {item.label && <div style={{ fontWeight: 600, color: '#fff' }}>{item.label}</div>}
                    <div>{formatCapture(item.captured_at)}</div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{index + 1} / {items.length}</div>
                </div>
            </div>
            {items.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); onNext(); }}
                    style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                             background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                             color: '#fff', cursor: 'pointer', padding: 8 }}>
                    <ChevronRight size={20} />
                </button>
            )}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
const ContaminationChart = ({
    bins, timeRange, className, title,
    customDateRange = [{ startDate: null, endDate: null, key: 'selection' }],
    isCustomDateRange = false,
    isAdmin = false
}) => {
    const chartRef         = useRef(null);
    const svgRef           = useRef(null);
    const cardRef          = useRef(null);
    const overlayHideTimer = useRef(null);

    const [chartData, setChartData]                   = useState(null);
    const [allMedia, setAllMedia]                     = useState([]);
    const [allCleanMedia, setAllCleanMedia]           = useState([]);
    const [isLoading, setIsLoading]                   = useState(true);
    const [error, setError]                           = useState(null);
    const [isDarkMode, setIsDarkMode]                 = useState(false);
    const [containerHeight]                           = useState(280);
    const [expandedLegendItem, setExpandedLegendItem] = useState(null);
    const [viewMode, setViewMode]                     = useState('percentage');
    const [imageOverlay, setImageOverlay]             = useState(null);
    const [lightbox, setLightbox]                     = useState(null);

    const [signedUrls, setSignedUrls]     = useState({});
    const [visibleCount, setVisibleCount] = useState(9);
    const signingInFlight = useRef(new Set());

    const hideImageOverlay = useCallback(() => setImageOverlay(null), []);
    const { width: containerWidth = 0 } = useResizeObserver({ ref: chartRef });

    const isBelgianContext = (() => {
        if (!bins?.length) return false;
        return bins.filter(b => BELGIAN_BIN_IDS.includes(String(b))).length > bins.length / 2;
    })();

    const wasteStreams = [
        { name: 'PMD',      color: isBelgianContext ? '#4084f4' : '#ffcc14' },
        { name: 'Organic',  color: '#18a44c' },
        { name: 'Paper',    color: isBelgianContext ? '#ffcc14' : '#4084f4' },
        { name: 'Residual', color: '#f05c0c' },
    ];

    // GET instead of POST — read-only, no CSRF needed, no logout risk
    const requestSign = useCallback((paths) => {
        const unique = [...new Set(paths)].filter(
            p => p && !signedUrls[p] && !signingInFlight.current.has(p)
        );
        if (!unique.length) return;
        const batch = unique.slice(0, 9); // hard cap matches GeneratePresignedUrlsBatchView
        batch.forEach(p => signingInFlight.current.add(p));

        axiosInstance.get('/api/media/sign-batch/', { params: { paths: batch.join(',') } })
            .then(res => setSignedUrls(prev => ({ ...prev, ...res.data.urls })))
            .catch(() => {})
            .finally(() => batch.forEach(p => signingInFlight.current.delete(p)));
    }, [signedUrls]);

    // ── Overlays ──────────────────────────────────────────────────────────────
    const showStreamOverlay = useCallback((streamName, event, stats = null) => {
        clearTimeout(overlayHideTimer.current);
        const images = isAdmin
            ? allMedia.filter(m => mediaMatchesStream(m, streamName))
            : [];
        setImageOverlay({ visible: true, x: event.clientX, y: event.clientY,
                          streamName: `${streamName} – Contaminated`, isClean: false, stats, images });
    }, [allMedia, isAdmin]);

    const showCleanStreamOverlay = useCallback((streamName, event, stats = null) => {
        clearTimeout(overlayHideTimer.current);
        const images = isAdmin
            ? allCleanMedia.filter(m => mediaMatchesStream(m, streamName))
            : [];
        setImageOverlay({ visible: true, x: event.clientX, y: event.clientY,
                          streamName: `${streamName} – Clean`, isClean: true, stats, images });
    }, [allCleanMedia, isAdmin]);

    useEffect(() => { return () => { d3.select(`#${TOOLTIP_ID}`).remove(); }; }, []);

    // ── Weight helpers ────────────────────────────────────────────────────────
    const getTotalWeight      = (name, data) => data?.per_bin_data?.reduce((t, b) => { const s = b.waste_streams.find(w => w.waste_stream === name); return t + (s?.total_weight_kg || 0); }, 0) || 0;
    const getCleanWeight      = (name, data) => data?.per_bin_data?.reduce((t, b) => { const s = b.waste_streams.find(w => w.waste_stream === name); return t + ((s?.total_weight_kg || 0) - (s?.contaminated_weight_kg || 0)); }, 0) || 0;
    const getContamWeight     = (name, data) => data?.per_bin_data?.reduce((t, b) => { const s = b.waste_streams.find(w => w.waste_stream === name); return t + (s?.contaminated_weight_kg || 0); }, 0) || 0;
    const getContamRateWeight = (name, data) => { const tot = getTotalWeight(name, data); const con = getContamWeight(name, data); return tot > 0 ? (con / tot) * 100 : 0; };
    const getCleanRateWeight  = (name, data) => 100 - getContamRateWeight(name, data);

    const formatName = (name) => name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    useEffect(() => {
        const check = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
        check();
        const obs = new MutationObserver(check);
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);

    const getBg = () => isDarkMode ? '#100c0c' : '#ffffff';

    useEffect(() => {
        setIsLoading(true); setChartData(null); setAllMedia([]); setAllCleanMedia([]);
        setError(null); setImageOverlay(null);
        setSignedUrls({});
        if (chartRef.current) d3.select(chartRef.current).selectAll('*').remove();
    }, [bins, timeRange, customDateRange, isCustomDateRange]);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!bins || bins.length === 0) { setError('No bins selected'); setIsLoading(false); return; }
                const params = new URLSearchParams();
                if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                    const toLocalDate = (d) => {
                        const ad = new Date(d.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
                        const pad = (n) => String(n).padStart(2, '0');
                        return `${ad.getFullYear()}-${pad(ad.getMonth() + 1)}-${pad(ad.getDate())}`;
                    };
                    const s = customDateRange[0].startDate instanceof Date ? customDateRange[0].startDate : new Date(customDateRange[0].startDate);
                    const e = customDateRange[0].endDate   instanceof Date ? customDateRange[0].endDate   : new Date(customDateRange[0].endDate);
                    params.append('start_date', toLocalDate(s));
                    params.append('end_date',   toLocalDate(e));
                } else {
                    params.append('period', timeRange || 'week');
                }
                bins.forEach(b => params.append('bin', b));

                const [ratesRes, mediaRes, cleanMediaRes] = await Promise.all([
                    axiosInstance.get(`/contamination-rates/?${params.toString()}`),
                    axiosInstance.get(`contamination_media/?${params.toString()}`),
                    axiosInstance.get(`contamination_media/?${params.toString()}&is_clean=true`),
                ]);

                if (ratesRes.data?.contamination_rates) setChartData(ratesRes.data);
                else setError('Invalid data format');
                setAllMedia(mediaRes.data?.media ?? []);
                setAllCleanMedia(cleanMediaRes.data?.media ?? []);
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to load data');
                setChartData(null);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [bins, timeRange, customDateRange, isCustomDateRange]);

    useEffect(() => {
        if (isLoading && chartRef.current) { d3.select(chartRef.current).selectAll('*').remove(); hideTooltip(); }
    }, [isLoading]);

    const buildWSD = (wasteStream, item, data) => ({
        wasteStream: wasteStream.name,
        color: wasteStream.color,
        translatedName: wasteStream.name,
        contaminationRate: item.contamination_rate,
        contaminatedItems: item.contaminated_items,
        totalItems: item.total_items,
        cleanRate: 100 - item.contamination_rate,
        hasData: true,
        contaminatedSubclasses: item.contaminated_subclasses || [],
        totalWeightKg: getTotalWeight(wasteStream.name, data),
        cleanWeightKg: getCleanWeight(wasteStream.name, data),
        contaminatedWeightKg: getContamWeight(wasteStream.name, data),
        contaminationRateByWeight: getContamRateWeight(wasteStream.name, data),
        cleanRateByWeight: getCleanRateWeight(wasteStream.name, data),
    });

    // ── Individual bar (expanded legend) ──────────────────────────────────────
    const renderIndividualBar = useCallback((wasteStreamData, containerElement) => {
        if (!wasteStreamData.hasData) return;
        d3.select(containerElement).selectAll('*').remove();

        const width = 280; const height = 40;
        const margin = { top: 10, right: 40, bottom: 10, left: 10 };
        const chartWidth = width - margin.left - margin.right;

        const svg = d3.select(containerElement).append('svg').attr('width', width).attr('height', height);
        const g   = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

        let cleanRate, contaminationRate;
        if (viewMode === 'kg' && wasteStreamData.totalWeightKg > 0) {
            cleanRate         = wasteStreamData.cleanRateByWeight;
            contaminationRate = wasteStreamData.contaminationRateByWeight;
        } else {
            cleanRate         = wasteStreamData.cleanRate;
            contaminationRate = wasteStreamData.contaminationRate;
        }

        const cleanWidth        = (cleanRate / 100)         * chartWidth;
        const contaminatedWidth = (contaminationRate / 100) * chartWidth;
        const barHeight = 20;

        const backgroundBar = g.append('rect').attr('x', 0).attr('y', 10)
            .attr('width', chartWidth).attr('height', barHeight)
            .attr('fill', wasteStreamData.color).attr('opacity', 0.3).attr('rx', 2);

        const cleanBar = g.append('rect').attr('x', 0).attr('y', 10)
            .attr('width', cleanWidth).attr('height', barHeight)
            .attr('fill', wasteStreamData.color).attr('rx', 2).style('cursor', 'pointer');

        let contaminatedBar;
        if (contaminationRate > 0) {
            contaminatedBar = g.append('rect').attr('x', cleanWidth).attr('y', 10)
                .attr('width', contaminatedWidth).attr('height', barHeight)
                .attr('fill', isDarkMode ? '#dc2626' : '#ef4444').attr('rx', 2).style('cursor', 'pointer');
        }

        cleanBar
            .on('mouseover', function(e) {
                const stats = {
                    streamName: `${wasteStreamData.translatedName} – Clean`,
                    rate: (viewMode === 'kg' ? cleanRate : wasteStreamData.cleanRate).toFixed(1),
                    contaminatedDisplay: viewMode === 'kg'
                        ? `${wasteStreamData.cleanWeightKg.toFixed(2)} kg`
                        : `${wasteStreamData.totalItems - wasteStreamData.contaminatedItems} items`,
                    total: viewMode === 'kg'
                        ? `${wasteStreamData.totalWeightKg.toFixed(2)} kg`
                        : `${wasteStreamData.totalItems} items`,
                    subclasses: [],
                };
                showCleanStreamOverlay(wasteStreamData.wasteStream, e, stats);
            })
            .on('mouseleave', function() {
                overlayHideTimer.current = setTimeout(() => setImageOverlay(null), 800);
            });

        if (contaminationRate > 0 && contaminatedBar) {
            contaminatedBar
                .on('mouseover', function(e) {
                    const stats = {
                        streamName: `${wasteStreamData.translatedName} – Contaminated`,
                        rate: (viewMode === 'kg' ? contaminationRate : wasteStreamData.contaminationRate).toFixed(1),
                        contaminatedDisplay: viewMode === 'kg'
                            ? `${wasteStreamData.contaminatedWeightKg.toFixed(2)} kg`
                            : `${wasteStreamData.contaminatedItems} items`,
                        total: viewMode === 'kg'
                            ? `${wasteStreamData.totalWeightKg.toFixed(2)} kg`
                            : `${wasteStreamData.totalItems} items`,
                        subclasses: wasteStreamData.contaminatedSubclasses.map(s => ({
                            label: formatName(s.name), count: s.count, weight_kg: s.weight_kg,
                        })),
                    };
                    showStreamOverlay(wasteStreamData.wasteStream, e, stats);
                })
                .on('mouseleave', function() {
                    overlayHideTimer.current = setTimeout(() => setImageOverlay(null), 800);
                });
        }

        const makeTotalHtml = () => `
            <div>
                <div style="font-weight:600;margin-bottom:6px;font-size:15px;">${wasteStreamData.translatedName} – Total</div>
                <div>Total Items: <b>${wasteStreamData.totalItems} items</b></div>
                ${viewMode === 'kg' && wasteStreamData.totalWeightKg > 0 ? `<div>Total Weight: <b>${wasteStreamData.totalWeightKg.toFixed(2)} kg</b></div>` : ''}
                <div>Clean: ${wasteStreamData.totalItems - wasteStreamData.contaminatedItems} items (${wasteStreamData.cleanRate.toFixed(1)}%)</div>
                <div>Contaminated: ${wasteStreamData.contaminatedItems} items (${wasteStreamData.contaminationRate.toFixed(1)}%)</div>
            </div>`;

        backgroundBar.on('mouseover', e => showTooltip(e, makeTotalHtml(), isDarkMode))
                     .on('mousemove', moveTooltip).on('mouseleave', hideTooltip);
    }, [viewMode, isDarkMode, showStreamOverlay, showCleanStreamOverlay, isAdmin]);

    useEffect(() => {
        if (!chartData?.contamination_rates || !expandedLegendItem) return;
        const wasteStream = wasteStreams.find(ws => ws.name === expandedLegendItem);
        if (!wasteStream) return;
        const item = chartData.contamination_rates.find(r => r.waste_stream.toLowerCase() === wasteStream.name.toLowerCase());
        if (!item) return;
        const container = document.getElementById(`bar-${wasteStream.name}`);
        if (!container) return;
        const data = buildWSD(wasteStream, item, chartData);
        d3.select(container).selectAll('*').remove();
        renderIndividualBar(data, container);
    }, [viewMode, expandedLegendItem, chartData, isDarkMode, renderIndividualBar]);

    // ── Circular chart ────────────────────────────────────────────────────────
    const renderCircularChart = () => {
        if (isLoading || error || !chartData?.contamination_rates?.length || !chartRef.current || !containerWidth) return;

        d3.select(chartRef.current).selectAll('*').remove();

        const contaminationRates = chartData.contamination_rates || [];
        const width = containerWidth; const height = containerHeight;
        const centerX = width / 2; const centerY = height / 2;
        const outerRadius  = Math.min(width, height) * 0.4;
        const isSmallChart = width < 300 || height < 250;
        const labelOffset  = isSmallChart ? Math.max(120, outerRadius + 30) : 100;

        const svg = d3.select(chartRef.current).append('svg').attr('width', width).attr('height', height);
        svgRef.current = svg.node();

        const arcGenerator             = d3.arc().startAngle(0).endAngle(3 * Math.PI / 2).cornerRadius(2);
        const contaminationArcGenerator = d3.arc().startAngle(0).cornerRadius(2);

        wasteStreams.forEach((wasteStream, index) => {
            const innerRadius     = outerRadius - (index + 1) * 18 - 14;
            const outerRadiusRing = outerRadius - (index + 1) * 18;

            const item = contaminationRates.find(r => r.waste_stream.toLowerCase() === wasteStream.name.toLowerCase());
            const totalWeightKg        = getTotalWeight(wasteStream.name, chartData);
            const contaminatedWeightKg = getContamWeight(wasteStream.name, chartData);
            const cleanWeightKg        = getCleanWeight(wasteStream.name, chartData);

            let contaminationValue, totalValue;
            if (viewMode === 'kg' && totalWeightKg > 0) {
                contaminationValue = contaminatedWeightKg; totalValue = totalWeightKg;
            } else {
                contaminationValue = item ? item.contamination_rate : 0; totalValue = 100;
            }

            const backgroundRing = svg.append('path')
                .datum({ innerRadius, outerRadius: outerRadiusRing })
                .attr('d', arcGenerator)
                .attr('fill', wasteStream.color)
                .attr('transform', `translate(${centerX}, ${centerY})`)
                .style('cursor', 'pointer');

            if (contaminationValue > 0) {
                const contPct   = viewMode === 'kg' ? (contaminationValue / totalValue) * 100 : contaminationValue;
                const contAngle = (contPct / 100) * (3 * Math.PI / 2);

                svg.append('path')
                    .datum({ innerRadius, outerRadius: outerRadiusRing, endAngle: contAngle })
                    .attr('d', contaminationArcGenerator)
                    .attr('fill', isDarkMode ? '#FFFFFF' : '#000000')
                    .attr('transform', `translate(${centerX}, ${centerY})`)
                    .style('cursor', 'pointer')
                    .on('mouseover', function(event) {
                        if (!item) return;
                        const stats = {
                            streamName: `${wasteStream.name} – Contaminated`,
                            rate: viewMode === 'kg'
                                ? getContamRateWeight(wasteStream.name, chartData).toFixed(1)
                                : item.contamination_rate.toFixed(1),
                            contaminatedDisplay: viewMode === 'kg'
                                ? `${contaminatedWeightKg.toFixed(2)} kg`
                                : `${item.contaminated_items} items`,
                            total: viewMode === 'kg'
                                ? `${totalWeightKg.toFixed(2)} kg`
                                : `${item.total_items} items`,
                            subclasses: (item.contaminated_subclasses || []).map(s => ({
                                label: formatName(s.name), count: s.count, weight_kg: s.weight_kg,
                            })),
                        };
                        showStreamOverlay(wasteStream.name, event, stats);
                    })
                    .on('mouseleave', function() {
                        overlayHideTimer.current = setTimeout(() => setImageOverlay(null), 800);
                    });
            }

            backgroundRing
                .on('mouseover', function(event) {
                    const cleanItems      = item ? (item.total_items - item.contaminated_items) : 0;
                    const cleanPercentage = item ? (100 - item.contamination_rate) : 100;
                    const stats = {
                        streamName: `${wasteStream.name} – Clean`,
                        rate: cleanPercentage.toFixed(1),
                        contaminatedDisplay: viewMode === 'kg' && cleanWeightKg > 0
                            ? `${cleanWeightKg.toFixed(2)} kg`
                            : `${cleanItems} items`,
                        total: viewMode === 'kg'
                            ? `${totalWeightKg.toFixed(2)} kg`
                            : `${item ? item.total_items : 0} items`,
                        subclasses: [],
                    };
                    showCleanStreamOverlay(wasteStream.name, event, stats);
                })
                .on('mouseleave', function() {
                    overlayHideTimer.current = setTimeout(() => setImageOverlay(null), 800);
                });

            let labelX = isSmallChart ? centerX - outerRadius + 70 : centerX - outerRadius + labelOffset;
            let labelY = centerY - outerRadius + index * 18 + 25;
            if (labelX < 10) labelX = 10;
            if (labelY < 15) labelY = 15;
            if (labelY > height - 15) labelY = height - 15;

            svg.append('text')
                .attr('x', labelX).attr('y', labelY)
                .attr('text-anchor', 'end').attr('dominant-baseline', 'middle')
                .attr('font-size', isSmallChart ? '10px' : '11px')
                .attr('fill', isDarkMode ? '#E5E7EB' : '#374151')
                .attr('pointer-events', 'none')
                .text(wasteStream.name);
        });

        const rates = wasteStreams.map(ws => {
            const item = contaminationRates.find(r => r.waste_stream.toLowerCase() === ws.name.toLowerCase());
            return item ? item.contamination_rate : null;
        }).filter(r => r !== null);
        const avgRate = rates.length > 0 ? rates.reduce((s, r) => s + r, 0) / rates.length : 0;
        const centerVal = viewMode === 'kg' && chartData.total_contaminated_weight_kg
            ? `${chartData.total_contaminated_weight_kg.toFixed(2)} kg`
            : `${avgRate.toFixed(1)}%`;

        svg.append('text')
            .attr('x', centerX).attr('y', centerY)
            .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
            .attr('font-size', isSmallChart ? '10px' : '12px').attr('font-weight', 'bold')
            .attr('fill', isDarkMode ? '#FFFFFF' : '#000000').attr('pointer-events', 'none')
            .text(centerVal);
    };

    useEffect(() => { renderCircularChart(); },
        [chartData, allMedia, allCleanMedia, isLoading, error, isDarkMode, containerHeight, containerWidth, viewMode]);

    useEffect(() => {
        setVisibleCount(9);
    }, [imageOverlay?.streamName]);

    useEffect(() => {
        if (!imageOverlay?.images?.length) return;
        requestSign(imageOverlay.images.slice(0, visibleCount).map(img => img.file_path));
    }, [imageOverlay, visibleCount, requestSign]);

    const handleLegendClick    = (name) => setExpandedLegendItem(prev => prev === name ? null : name);
    const handleViewModeChange = (mode) => setViewMode(mode);

    // ── CSV Export ────────────────────────────────────────────────────────────
    const handleExportCSV = async () => {
        if (!chartData?.contamination_rates) return;
        const headers = ['Waste Stream','Total Items','Clean Items','Clean %','Contaminated Items','Contamination %',
            'Total Weight (kg)','Clean Weight (kg)','Contaminated Weight (kg)','Contamination % by Weight',
            'Contaminant','Contaminant Count','Contaminant Weight (kg)'];
        const rows = [];
        chartData.contamination_rates.forEach(item => {
            const cleanItems  = item.total_items - item.contaminated_items;
            const cleanPct    = 100 - item.contamination_rate;
            const totWkg      = getTotalWeight(item.waste_stream, chartData);
            const cleanWkg    = getCleanWeight(item.waste_stream, chartData);
            const contWkg     = getContamWeight(item.waste_stream, chartData);
            const contRateWkg = getContamRateWeight(item.waste_stream, chartData);
            if (item.contaminated_subclasses?.length) {
                item.contaminated_subclasses.forEach((sub, idx) => {
                    rows.push([
                        idx === 0 ? item.waste_stream : '', idx === 0 ? item.total_items : '',
                        idx === 0 ? cleanItems : '', idx === 0 ? cleanPct.toFixed(2) : '',
                        idx === 0 ? item.contaminated_items : '', idx === 0 ? item.contamination_rate.toFixed(2) : '',
                        idx === 0 ? totWkg.toFixed(3) : '', idx === 0 ? cleanWkg.toFixed(3) : '',
                        idx === 0 ? contWkg.toFixed(3) : '', idx === 0 ? contRateWkg.toFixed(2) : '',
                        formatName(sub.name), sub.count, (sub.weight_kg || 0).toFixed(3)
                    ]);
                });
            } else {
                rows.push([item.waste_stream, item.total_items, cleanItems, cleanPct.toFixed(2),
                    item.contaminated_items, item.contamination_rate.toFixed(2),
                    totWkg.toFixed(3), cleanWkg.toFixed(3), contWkg.toFixed(3), contRateWkg.toFixed(2), '', '', '']);
            }
        });
        let csv = `# Contamination Rates Export\n# Export Date: ${new Date().toISOString().split('T')[0]}\n`;
        csv += `# Period: ${timeRange}\n# Bins: ${Array.isArray(bins) ? bins.join(', ') : bins}\n`;
        csv += `# Overall Contamination Rate (by count): ${chartData.overall_contamination_rate.toFixed(2)}%\n`;
        csv += `# Overall Contamination Rate (by weight): ${(chartData.overall_contamination_rate_by_weight || 0).toFixed(2)}%\n\n`;
        csv += headers.join(',') + '\n';
        csv += rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');

        let section2 = '';
        try {
            const params = { bins: Array.isArray(bins) ? bins.join(',') : bins };
            if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                const s = customDateRange[0].startDate; const e = customDateRange[0].endDate;
                params.startDate = (s instanceof Date ? s : new Date(s)).toISOString().slice(0, 10);
                params.endDate   = (e instanceof Date ? e : new Date(e)).toISOString().slice(0, 10);
            } else { params.timeRange = timeRange; }
            const response = await axiosInstance.get('/stats_v2/raw-data', { params, paramsSerializer: { indexes: null } });
            if (response.data && Array.isArray(response.data)) {
                const contaminated  = response.data.filter(item => item.contamination === 'Y');
                const detailHeaders = ['Bin','Date & Time','Item','Thrown Into','Expected Stream','Contamination Y/N','Weight'].join(',');
                const detailRows    = contaminated.map(item => {
                    let displayTime = '-';
                    if (item.time_collected && item.time_collected !== '-') {
                        try { displayTime = new Date(item.time_collected).toLocaleString('nl-NL', {
                            timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
                        }); } catch { displayTime = item.time_collected; }
                    }
                    return [item.bin||'-', `"${displayTime}"`, item.sub_class_waste_type||'-', item.waste_type||'-',
                            item.waste_weight||0, item.expected_stream||'-', item.contamination||'-'].join(',');
                });
                section2 = ['\n\n','"--------------------------------------------------"',
                    '"--- SECTION 2: CONTAMINATION EVENTS ---"','',detailHeaders,...detailRows].join('\n');
            }
        } catch { section2 = '\n\n"Could not retrieve individual contamination records at this time."'; }

        const blob = new Blob([csv + section2], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `contamination_rates_${timeRange}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link); link.click();
        document.body.removeChild(link); URL.revokeObjectURL(url);
    };

    const handleExportPNG = async () => {
        if (!cardRef.current) return;
        try {
            const htmlToImage = await import('html-to-image');
            const currentExpanded = expandedLegendItem;
            setExpandedLegendItem(null);
            await new Promise(r => setTimeout(r, 300));
            const dataUrl = await htmlToImage.toPng(cardRef.current, { backgroundColor: getBg(), pixelRatio: 2, cacheBust: true });
            const link = document.createElement('a');
            link.download = `contamination_chart_${timeRange}_${new Date().toISOString().split('T')[0]}.png`;
            link.href = dataUrl; link.click();
            if (currentExpanded) setTimeout(() => setExpandedLegendItem(currentExpanded), 300);
        } catch (err) { console.error('Error exporting PNG:', err); }
    };

    if (isLoading) return (
        <Card className={`flex flex-col h-full ${className || ''}`} style={{ backgroundColor: getBg() }}>
            <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                <h2 className="text-lg font-medium text-center text-gray-900 dark:text-white">{title || 'Contamination Rates'}</h2>
                <div className="flex-1 flex items-center justify-center" style={{ height: `${containerHeight}px` }}>
                    <div className="animate-pulse w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700" />
                </div>
            </CardContent>
        </Card>
    );

    if (error) return (
        <Card className={`flex flex-col h-full ${className || ''}`} style={{ backgroundColor: getBg() }}>
            <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                <h2 className="text-lg font-medium mb-4 text-center text-gray-900 dark:text-white">{title || 'Contamination Rates'}</h2>
                <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">Error</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 px-4">{error}</p>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <>
        <Card ref={cardRef} className={`flex flex-col h-full ${className || ''}`}
              style={{ backgroundColor: getBg() }} data-card-export="true">
            <CardContent className="flex-1 flex flex-col p-3 sm:p-4">
                <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
                    <div className="flex-1 text-center">
                        <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">{title || 'Contamination Rates'}</h2>
                        {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                            <p className="text-xs text-muted-foreground mt-1">
                                {(() => {
                                    const s = customDateRange[0].startDate; const e = customDateRange[0].endDate;
                                    const sd = s instanceof Date ? s : new Date(s);
                                    const ed = e instanceof Date ? e : new Date(e);
                                    if (!isNaN(sd) && !isNaN(ed)) return `${sd.toLocaleDateString()} - ${ed.toLocaleDateString()}`;
                                    return '';
                                })()}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2 ml-auto sm:ml-0">
                        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden shrink-0">
                            <button onClick={() => handleViewModeChange('percentage')}
                                className={`px-2 py-1 text-xs font-medium transition-colors ${viewMode === 'percentage' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}>%</button>
                            <button onClick={() => handleViewModeChange('kg')}
                                className={`px-2 py-1 text-xs font-medium transition-colors ${viewMode === 'kg' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                                disabled={!chartData?.total_weight_kg}>KG</button>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary h-8 px-2 shrink-0">
                                    <Download className="h-4 w-4 mr-1" /><span className="text-xs">Export</span><ChevronDown className="h-3 w-3 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                <div className="flex-1 relative overflow-hidden" style={{ height: `${containerHeight}px` }}>
                    <div ref={chartRef} className="w-full h-full p-0" />
                </div>

                {/* Legend */}
                <div className="mt-4 space-y-2 text-xs">
                    <h3 className="font-semibold text-center mb-2 text-gray-900 dark:text-white">
                        Legend <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">(Click to expand details)</span>
                    </h3>
                    <div className="space-y-2">
                        {wasteStreams.map((wasteStream, index) => {
                            const item = chartData?.contamination_rates?.find(r =>
                                r.waste_stream.toLowerCase() === wasteStream.name.toLowerCase());
                            if (!item) return (
                                <div key={`${wasteStream.name}-${index}`} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800">
                                        <div className="flex items-center min-w-0 flex-1">
                                            <div className="w-4 h-4 mr-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: wasteStream.color }} />
                                            <span className="text-sm font-medium">{wasteStream.name}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">No Data</div>
                                    </div>
                                </div>
                            );
                            const wsd        = buildWSD(wasteStream, item, chartData);
                            const isExpanded = expandedLegendItem === wasteStream.name;
                            const displayValue   = viewMode === 'kg' && wsd.totalWeightKg > 0
                                ? `${wsd.contaminatedWeightKg.toFixed(2)} kg`
                                : `${wsd.contaminationRate.toFixed(1)}%`;
                            const displaySubtext = `${wsd.contaminatedItems}/${wsd.totalItems} items`;

                            return (
                                <div key={`${wasteStream.name}-${viewMode}-${index}`} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                         onClick={() => handleLegendClick(wasteStream.name)}>
                                        <div className="flex items-center min-w-0 flex-1">
                                            <div className="w-4 h-4 mr-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: wasteStream.color }} />
                                            <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} text-sm font-medium truncate`}>{wsd.translatedName}</span>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <div className="text-right">
                                                <div className="font-medium text-sm whitespace-nowrap">{displayValue}</div>
                                                <div className="text-xs text-gray-500 whitespace-nowrap">{displaySubtext}</div>
                                            </div>
                                            <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="p-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                                            <div id={`bar-${wasteStream.name}`} className="flex justify-center" />
                                            {isAdmin && (
                                                <p className="text-[10px] text-center text-gray-400 mt-1">Hover bar segments to see images</p>
                                            )}
                                            <div className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <div className="space-y-1">
                                                    {viewMode === 'kg' && wsd.totalWeightKg > 0 ? (
                                                        <>
                                                            <div><strong>Total:</strong> {wsd.totalWeightKg.toFixed(2)} kg ({wsd.totalItems} items)</div>
                                                            <div><strong>Clean:</strong> {wsd.cleanWeightKg.toFixed(2)} kg ({wsd.cleanRateByWeight.toFixed(1)}%) - {wsd.totalItems - wsd.contaminatedItems} items</div>
                                                            <div><strong>Contaminated:</strong> {wsd.contaminatedWeightKg.toFixed(2)} kg ({wsd.contaminationRateByWeight.toFixed(1)}%) - {wsd.contaminatedItems} items</div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div><strong>Total:</strong> {wsd.totalItems} items{wsd.totalWeightKg > 0 && ` (${wsd.totalWeightKg.toFixed(2)} kg)`}</div>
                                                            <div><strong>Clean:</strong> {wsd.totalItems - wsd.contaminatedItems} items ({wsd.cleanRate.toFixed(1)}%){wsd.cleanWeightKg > 0 && ` (${wsd.cleanWeightKg.toFixed(2)} kg)`}</div>
                                                            <div><strong>Contaminated:</strong> {wsd.contaminatedItems} items ({wsd.contaminationRate.toFixed(1)}%){wsd.contaminatedWeightKg > 0 && ` (${wsd.contaminatedWeightKg.toFixed(2)} kg)`}</div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center mb-1 flex-wrap">
                            <div className="w-3 h-3 mr-1 bg-black dark:bg-white flex-shrink-0" />
                            <span className="text-xs text-center">Black/White overlay shows contamination</span>
                        </div>
                        <div className="text-xs text-center px-2 text-gray-500">Colored bars show the clean waste portion</div>
                    </div>
                    {chartData?.per_bin_data?.length > 1 && (
                        <div className="mt-2 text-xs px-2 text-center text-gray-600 dark:text-gray-400">
                            <details className="mt-2">
                                <summary className="cursor-pointer text-xs font-medium">Per-Bin Breakdown</summary>
                                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                                    {chartData.per_bin_data.map((binData, i) => (
                                        <div key={i} className="text-xs p-1 break-words">
                                            <strong>{binData.bin}:</strong> {binData.contamination_rate.toFixed(1)}%
                                            ({binData.contaminated_items}/{binData.total_items} items)
                                            {viewMode === 'kg' && binData.total_weight_kg && (
                                                <> | {binData.contaminated_weight_kg?.toFixed(2)} kg / {binData.total_weight_kg?.toFixed(2)} kg</>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>

        {/* ── Image overlay ── */}
        {imageOverlay?.visible && (
            <div
                onMouseEnter={() => clearTimeout(overlayHideTimer.current)}
                onMouseLeave={() => { overlayHideTimer.current = setTimeout(() => setImageOverlay(null), 300); }}
                style={{
                    position: 'fixed',
                    left: Math.min(imageOverlay.x + 16, window.innerWidth - 290),
                    top:  Math.min(imageOverlay.y - 10, window.innerHeight - 400),
                    zIndex: 10000,
                    background: isDarkMode ? 'rgba(20,20,20,0.97)' : 'rgba(255,255,255,0.97)',
                    border: isDarkMode ? '1px solid #444' : '1px solid #ddd',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    padding: 12, width: 270, pointerEvents: 'auto',
                    fontSize: 13, color: isDarkMode ? '#fff' : '#111', lineHeight: 1.5,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{imageOverlay.streamName}</span>
                    <button onClick={hideImageOverlay}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDarkMode ? '#aaa' : '#666', padding: 2 }}>
                        <X size={14} />
                    </button>
                </div>

                {imageOverlay.stats && (
                    <div style={{ marginBottom: isAdmin ? 10 : 0 }}>
                        <div>Total: <b>{imageOverlay.stats.total}</b></div>
                        <div>{imageOverlay.isClean ? 'Clean Rate' : 'Contamination Rate'}: <b>{imageOverlay.stats.rate}%</b></div>
                        <div>{imageOverlay.isClean ? 'Clean items' : 'Contaminated items'}: <b>{imageOverlay.stats.contaminatedDisplay}</b></div>
                        {!imageOverlay.isClean && imageOverlay.stats.subclasses?.length > 0 && (
                            <div style={{ marginTop: 6 }}>
                                <div style={{ fontWeight: 600, marginBottom: 2 }}>Contaminants:</div>
                                {imageOverlay.stats.subclasses.map((s, i) => (
                                    <div key={i}>• {s.label}: {s.count} items</div>
                                ))}
                            </div>
                        )}
                        {!imageOverlay.isClean && imageOverlay.stats.subclasses?.length === 0 && (
                            <div style={{ color: isDarkMode ? '#888' : '#999', fontSize: 12 }}>No contamination details</div>
                        )}
                    </div>
                )}

                {isAdmin && (
                    <>
                        <div style={{ borderTop: isDarkMode ? '1px solid #333' : '1px solid #eee', marginBottom: 8, marginTop: 8 }} />
                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: isDarkMode ? '#ccc' : '#555' }}>
                            {imageOverlay.isClean ? 'Clean Stream Images' : 'Contamination Images'}
                        </div>

                        {imageOverlay.images.length === 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: isDarkMode ? '#888' : '#999', fontSize: 12, padding: '4px 0' }}>
                                <ImageOff size={14} /> No images for this period
                            </div>
                        ) : (
                            <>
                                <div
                                    onScroll={(e) => {
                                        const el = e.currentTarget;
                                        if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
                                            setVisibleCount(prev => Math.min(prev + 9, imageOverlay.images.length));
                                        }
                                    }}
                                    style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, maxHeight: 200, overflowY: 'auto' }}
                                >
                                    {imageOverlay.images.slice(0, visibleCount).map((img, idx) => {
                                        const src = signedUrls[img.file_path];
                                        const loading = img.file_path && !(img.file_path in signedUrls);
                                        return (
                                            <button key={img.id} onClick={() => setLightbox({ items: imageOverlay.images, index: idx })}
                                                style={{ position: 'relative', aspectRatio: '1', borderRadius: 6, overflow: 'hidden',
                                                         background: isDarkMode ? '#333' : '#f0f0f0', border: 'none', cursor: 'pointer', padding: 0 }}
                                                title={img.label || formatCapture(img.captured_at)}>
                                                {src ? (
                                                    img.media_type === 'video'
                                                        ? <video src={src} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        : <img src={src} alt={img.label || 'media'} loading="lazy"
                                                               style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : loading ? (
                                                    <div className="animate-pulse w-full h-full" style={{ background: isDarkMode ? '#444' : '#e0e0e0' }} />
                                                ) : (
                                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <ImageOff size={14} style={{ color: isDarkMode ? '#666' : '#bbb' }} />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p style={{ marginTop: 6, fontSize: 10, color: isDarkMode ? '#666' : '#aaa' }}>
                                    Showing {Math.min(visibleCount, imageOverlay.images.length)} of {imageOverlay.images.length} · click to enlarge
                                </p>
                            </>
                        )}
                    </>
                )}
            </div>
        )}

        {isAdmin && lightbox && (
            <Lightbox
                items={lightbox.items} index={lightbox.index}
                onClose={() => setLightbox(null)}
                onPrev={() => setLightbox(prev => ({ ...prev, index: (prev.index - 1 + prev.items.length) % prev.items.length }))}
                onNext={() => setLightbox(prev => ({ ...prev, index: (prev.index + 1) % prev.items.length }))}
                signedUrls={signedUrls}
                onRequestSign={requestSign}
            />
        )}
        </>
    );
};

export default ContaminationChart;